/**
 * Schema Utilities for FormCrafter
 * Functions for converting and normalizing form schemas
 */

import { mapFieldType, isFileType } from './fieldTypeUtils';

/**
 * Converts JSON object to FormCrafter schema format
 * Handles legacy formBuilder format, current FormCrafter format, and comprehensive format
 *
 * @param {Object|string} jsonObject - The form schema (can be JSON string or object)
 * @param {number|string} subformId - Optional subform ID for title generation
 * @returns {Object} - Normalized FormCrafter schema with { title, description, fields }
 */
export const convertToFormCrafterSchema = (jsonObject, subformId) => {
  try {
    const formFields =
      typeof jsonObject === 'string' ? JSON.parse(jsonObject) : jsonObject;

    // Already in FormCrafter format with fields array
    if (formFields.fields && Array.isArray(formFields.fields)) {
      const fields = formFields.fields.map((field) => {
        const originalType = field.type;

        // Normalize file field types
        if (isFileType(originalType)) {
          const mappedType = mapFieldType(originalType);
          if (mappedType !== originalType) {
            return { ...field, type: mappedType };
          }
        }

        return field;
      });

      return { ...formFields, fields };
    }

    // Comprehensive format with schema property
    if (formFields.schema && formFields.schema.fields) {
      return formFields.schema;
    }

    // Convert from legacy formBuilder array format
    if (Array.isArray(formFields)) {
      const fields = formFields.map((field) => {
        const mappedType = mapFieldType(field.type);

        const baseField = {
          id: field.name || field.id || `field_${Math.random()}`,
          label: field.label || field.name || 'Field',
          type: mappedType,
          required:
            field.required === 'true' || field.required === true || false,
          placeholder: field.placeholder || '',
          helpText: field.description || field.subtype || '',
        };

        // Convert options for select/radio/checkbox fields
        if (field.values && field.values.length > 0) {
          baseField.options = field.values.map((val) => ({
            label: val.label || val.value,
            value: val.value,
          }));
        }

        // Preserve default value
        if (field.value !== undefined && field.value !== null) {
          baseField.value = field.value;
        }

        // Preserve conditions for conditional visibility
        if (field.conditions) {
          baseField.conditions = field.conditions;
        }

        return baseField;
      });

      return {
        title: `Form ${subformId || ''}`,
        description: '',
        fields,
      };
    }

    // Empty fallback
    return {
      title: `Form ${subformId || ''}`,
      description: '',
      fields: [],
    };
  } catch (error) {
    console.error('Error converting to FormCrafter schema:', error);
    return {
      title: 'Error Loading Form',
      description: 'Unable to parse form schema',
      fields: [],
    };
  }
};

/**
 * Extracts file fields from a schema
 * @param {Object} schema - FormCrafter schema with fields array
 * @param {Array} fileTypes - Array of file type strings to match
 * @returns {Array} - Array of file field objects
 */
export const getFileFieldsFromSchema = (
  schema,
  fileTypes = ['file', 'attachment', 'image', 'signature']
) => {
  if (!schema?.fields) return [];

  const fileFields = [];
  const processedFieldIds = new Set();

  // Process fields in tabs
  schema.fields.forEach((field) => {
    if (field.type === 'tabs' && field.tabContent) {
      Object.values(field.tabContent).forEach((tabItems) => {
        if (Array.isArray(tabItems)) {
          tabItems.forEach((item) => {
            if (item.type === 'field') {
              const nestedField = schema.fields.find(
                (f) => f.id === item.value
              );
              if (
                nestedField &&
                !processedFieldIds.has(nestedField.id) &&
                fileTypes.includes(nestedField.type)
              ) {
                fileFields.push(nestedField);
                processedFieldIds.add(nestedField.id);
              }
            }
          });
        }
      });
    }
  });

  // Process regular file fields
  schema.fields.forEach((field) => {
    if (!processedFieldIds.has(field.id) && fileTypes.includes(field.type)) {
      fileFields.push(field);
      processedFieldIds.add(field.id);
    }
  });

  return fileFields;
};
